import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError } from '#src/shared/error';

import { getViewerSignature } from '#src/app/shared/api/signature';

import type { PetitionViewerInfoQuery, PetitionViewerInfoQueryVariables } from './api.graphql';
import { PetitionViewerInfo as PetitionViewerInfoGqlQuery } from './api.graphql';

export type PetitionViewerInfo = {
	/**
	 * if the user is a guest, this will be undefined, for optimization purposes
	 */
	readonly petitionId?: string;
	readonly ownPetition: boolean;
	readonly canEdit: boolean;
	readonly signed: boolean;
};

/**
 * Fetches info about how the viewer (current user) relates to the petition.
 */
export async function getPetitionViewerInfo(
	utilityContext: UtilityContext,
	slug: string,
	currentUserId: string | undefined,
): Promise<PetitionViewerInfo> {
	// The currentUserId will be undefined for a guest viewer. The queries below can handle a missing ID on their own,
	// but short-circuiting those fetches can save us some time when the page is loading for guests (a very common
	// case).
	if (!currentUserId) {
		return {
			ownPetition: false,
			canEdit: false,
			signed: false,
		};
	}

	const [signature, petitionInfo] = await Promise.all([
		getViewerSignature(slug, utilityContext),
		getUserPetitionInfo(slug, utilityContext),
	]);

	const ownPetition = petitionInfo.user.id === currentUserId;
	const canEdit = petitionInfo.viewerCanEdit;

	return {
		petitionId: petitionInfo.id,
		ownPetition,
		canEdit,
		signed: !!signature,
	};
}

type PetitionViewerInfoQueryPetition = NonNullable<PetitionViewerInfoQuery['petition']>;

async function getUserPetitionInfo(
	slugOrId: string,
	{ gql: { fetch } }: UtilityContext,
): Promise<PetitionViewerInfoQueryPetition> {
	const { data } = await fetch<PetitionViewerInfoQuery, PetitionViewerInfoQueryVariables>({
		query: PetitionViewerInfoGqlQuery,
		variables: { slugOrId },
		rejectOnError: true,
	});
	if (!data.petition) {
		throw new NotFoundError();
	}
	return data.petition;
}
